import { useCustomerCategoryLabel } from '@/features/settings';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LocateFixed, MapPin, Navigation, RefreshCw } from 'lucide-react';
import {
  partnerMapOwnersSchema,
  partnerMapResponseSchema,
  partnerLocationDetailSchema,
  PARTNER_MAP_DEFAULT_STYLE,
  type PartnerMapViewport,
  type PartnerMapResponse,
  type PartnerLocationDetail,
  type PartnerLocation,
  type PartnerType,
} from '@erp/shared';
import { apiFetch } from '@/lib/api-client';
import { InlineHelp } from '@/features/help';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePermissions } from '@/features/auth';
import { useFeatureAvailability } from '@/features/platform-features';
import { CustomerCareTaskDialog } from '@/features/sales-assistant';
import { buildPartnerQuery, type PartnerListParams } from '../api/use-partners';
import { PartnerMapCanvas } from './partner-map-canvas';

export function PartnerMapFilters({
  type,
  value,
  onChange,
}: {
  type: PartnerType;
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const owners = useQuery({
    queryKey: ['partner-map-owners'],
    queryFn: ({ signal }) =>
      apiFetch('/api/v1/partners/map/owners', partnerMapOwnersSchema, { signal }),
  });
  const update = (key: string, next: string) => {
    const result = { ...value };
    if (next) result[key] = next;
    else delete result[key];
    onChange(result);
  };
  const roles = (value['roles'] ?? '').split(',').filter(Boolean);
  return (
    <details className="rounded-md border border-border p-3">
      <summary className="min-h-8 cursor-pointer text-sm font-medium">
        {t('partnerMap.filters')}
      </summary>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        {type === 'all' ? (
          <fieldset className="flex flex-wrap gap-3">
            <legend className="text-xs text-muted-foreground">{t('partnerMap.roles')}</legend>
            {['customer', 'prospect', 'supplier'].map((role) => (
              <label key={role} className="flex min-h-11 items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={roles.length === 0 || roles.includes(role)}
                  onChange={(event) => {
                    const current = roles.length ? roles : ['customer', 'prospect', 'supplier'];
                    const next = event.target.checked
                      ? [...current, role]
                      : current.filter((item) => item !== role);
                    if (next.length) update('roles', next.join(','));
                  }}
                />
                {t(`partnerMap.${role}`)}
              </label>
            ))}
          </fieldset>
        ) : null}
        <label className="text-sm">
          {t('partnerMap.locations')}
          <select
            className="ml-2 min-h-11 rounded border border-input bg-background px-2"
            value={value['locationMode'] ?? 'MAIN'}
            onChange={(event) => update('locationMode', event.target.value)}
          >
            {['MAIN', 'SHIPPING', 'ALL'].map((mode) => (
              <option key={mode} value={mode}>
                {t(`partnerMap.${mode}`)}
              </option>
            ))}
          </select>
        </label>
        {['city', 'postalCode', 'region', 'contactDays', 'visitDays'].map((key) => (
          <label key={key} className="text-xs text-muted-foreground">
            {t(`partnerMap.${key}`)}
            <Input
              type={key.endsWith('Days') ? 'number' : 'text'}
              min="1"
              className="mt-1 w-40"
              value={value[key] ?? ''}
              onChange={(event) => update(key, event.target.value)}
            />
          </label>
        ))}
        <label className="text-sm">
          {t('partnerMap.owner')}
          <select
            className="ml-2 min-h-11 rounded border border-input bg-background px-2"
            value={value['ownerUserId'] ?? ''}
            onChange={(event) => update('ownerUserId', event.target.value)}
          >
            <option value="">{t('partnerMap.all')}</option>
            {owners.data?.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.name}
              </option>
            ))}
          </select>
        </label>
        {['neverContacted', 'addressReview'].map((key) => (
          <label key={key} className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={value[key] === 'true'}
              onChange={(event) => update(key, event.target.checked ? 'true' : '')}
            />
            {t(`partnerMap.${key}`)}
          </label>
        ))}
        {value['radiusKm'] ? (
          <Button
            variant="outline"
            onClick={() => {
              const next = { ...value };
              delete next['nearLat'];
              delete next['nearLng'];
              delete next['radiusKm'];
              onChange(next);
            }}
          >
            {t('partnerMap.clearRadius')}
          </Button>
        ) : null}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{t('partnerMap.contactHelp')}</p>
    </details>
  );
}
export function PartnerMapPanel({
  params,
  ready,
  onPage,
  onFilters,
}: {
  params: PartnerListParams;
  ready: boolean;
  onPage: (page: number) => void;
  onFilters: (filters: Record<string, string>) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const categoryLabel = useCustomerCategoryLabel();
  const { can } = usePermissions();
  const features = useFeatureAvailability();
  const client = useQueryClient();
  const [viewport, setViewport] = useState<PartnerMapViewport>({
    west: -180,
    east: 180,
    south: -85,
    north: 85,
    zoom: 3,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ latitude: number; longitude: number; zoom?: number } | null>(
    null,
  );
  const [radius, setRadius] = useState('25');
  const [picking, setPicking] = useState<'radius' | 'manual' | null>(null);
  const [manual, setManual] = useState<{
    latitude: string;
    longitude: string;
    reason: string;
  } | null>(null);
  const [publicAddress, setPublicAddress] = useState(false);
  const [task, setTask] = useState(false);
  const [geoError, setGeoError] = useState(false);
  const filterKey = JSON.stringify({ ...params, page: 1 });
  useEffect(() => {
    setSelectedId(null);
    setManual(null);
    setTask(false);
  }, [filterKey]);
  const query = `${buildPartnerQuery(params)}&${new URLSearchParams(Object.fromEntries(Object.entries(viewport).map(([key, value]) => [key, String(value)])))}`;
  const results = useQuery<PartnerMapResponse>({
    queryKey: ['partner-map', query],
    refetchInterval: (query) =>
      query.state.data?.data.some((location) => ['PENDING', 'ERROR'].includes(location.status))
        ? 30000
        : false,
    queryFn: ({ signal }) =>
      apiFetch(`/api/v1/partners/map?${query}`, partnerMapResponseSchema, { signal }),
    enabled: ready,
  });
  const detail = useQuery<PartnerLocationDetail>({
    queryKey: ['partner-location', selectedId],
    refetchInterval: (query) =>
      ['PENDING', 'ERROR'].includes(query.state.data?.status ?? '') ? 15000 : false,
    queryFn: ({ signal }) =>
      apiFetch(`/api/v1/partners/map/locations/${selectedId}`, partnerLocationDetailSchema, {
        signal,
      }),
    enabled: selectedId !== null && ready,
  });
  const selected = detail.data;
  useEffect(() => {
    setPublicAddress(false);
  }, [selectedId]);
  const geocode = useMutation({
    mutationFn: () =>
      apiFetch(
        `/api/v1/partners/map/locations/${selectedId}/geocoding`,
        partnerLocationDetailSchema,
        {
          method: 'POST',
          body: { version: selected?.version, publicBusinessAddress: true },
        },
      ),
    onSuccess: async () => {
      setPublicAddress(false);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['partner-map'] }),
        client.invalidateQueries({ queryKey: ['partner-location'] }),
      ]);
    },
  });
  const position = useMutation({
    mutationFn: () =>
      apiFetch(`/api/v1/partners/map/locations/${selectedId}`, partnerLocationDetailSchema, {
        method: 'PATCH',
        body: {
          latitude: Number(manual?.latitude),
          longitude: Number(manual?.longitude),
          reason: manual?.reason,
          version: selected?.version,
        },
      }),
    onSuccess: async () => {
      setManual(null);
      setPicking(null);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['partner-map'] }),
        client.invalidateQueries({ queryKey: ['partner-location'] }),
      ]);
    },
  });
  const choose = (location: PartnerLocation) => {
    setSelectedId(location.id);
    setManual(null);
    if (location.latitude !== null && location.longitude !== null)
      setFocus({ latitude: location.latitude, longitude: location.longitude });
  };
  const origin = (latitude: number, longitude: number, distance = radius) => {
    onFilters({ nearLat: String(latitude), nearLng: String(longitude), radiusKm: distance });
    setPicking(null);
    setFocus({ latitude, longitude });
  };
  const markers = useMemo(() => results.data?.markers ?? [], [results.data]);
  const date = (value: string | null) =>
    value
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(value))
      : t('partnerMap.notRecorded');
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <InlineHelp articleId="partners.partner-map" label={t('partnerMap.title')} />
        <label>
          {t('partnerMap.radius')}{' '}
          <select
            className="min-h-11 rounded border border-input bg-background px-2"
            value={radius}
            onChange={(event) => {
              setRadius(event.target.value);
              if (params.mapFilters?.['nearLat'] && params.mapFilters['nearLng'])
                origin(
                  Number(params.mapFilters['nearLat']),
                  Number(params.mapFilters['nearLng']),
                  event.target.value,
                );
            }}
          >
            {['10', '25', '50'].map((km) => (
              <option key={km} value={km}>
                {km} km
              </option>
            ))}
          </select>
        </label>
        <Button
          variant="outline"
          onClick={() => setPicking(picking === 'radius' ? null : 'radius')}
          aria-pressed={picking === 'radius'}
        >
          <MapPin className="h-4 w-4" />
          {t('partnerMap.pickOrigin')}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setGeoError(false);
            if (!navigator.geolocation) {
              setGeoError(true);
              return;
            }
            navigator.geolocation.getCurrentPosition(
              (value) => origin(value.coords.latitude, value.coords.longitude),
              () => setGeoError(true),
              { timeout: 10000, maximumAge: 0 },
            );
          }}
        >
          <LocateFixed className="h-4 w-4" />
          {t('partnerMap.myPosition')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => void results.refetch()}
          disabled={results.isFetching}
        >
          <RefreshCw className="h-4 w-4" />
          {t('partnerMap.refresh')}
        </Button>
        <span className="text-xs text-muted-foreground">{t('partnerMap.airDistance')}</span>
      </div>
      {picking ? (
        <p role="status" className="text-sm">
          {t(`partnerMap.pick.${picking}`)}
        </p>
      ) : null}
      {geoError ? <p role="alert">{t('partnerMap.geoError')}</p> : null}
      {results.data?.configuration.geocoding === 'NOT_CONFIGURED' ? (
        <p className="rounded border border-border p-3 text-sm">{t('partnerMap.setup')}</p>
      ) : null}
      {results.isError ? (
        <p role="alert">
          {t('partnerMap.loadError')}{' '}
          <Button onClick={() => void results.refetch()}>{t('common.retry')}</Button>
        </p>
      ) : null}
      <div className="flex flex-wrap gap-4 text-xs" aria-label={t('partnerMap.legend')}>
        {['customer', 'prospect', 'supplier', 'mixed'].map((role, index) => (
          <span key={role} className="flex items-center gap-1">
            <span
              className={
                [
                  'h-3 w-3 rounded-full bg-chart-1',
                  'h-3 w-3 rounded-full bg-chart-2',
                  'h-3 w-3 rounded-full bg-chart-3',
                  'h-3 w-3 rounded-full bg-primary',
                ][index]
              }
            />
            {t(`partnerMap.${role}`)}
          </span>
        ))}
      </div>
      {results.data ? (
        <p role="status" className="text-sm">
          {t('partnerMap.counts', results.data.meta)} ·{' '}
          {t('partnerMap.updated', { date: date(results.data.generatedAt) })}
        </p>
      ) : (
        <div className="h-6 animate-pulse rounded bg-muted" />
      )}
      <div className="grid min-w-0 gap-3 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <PartnerMapCanvas
          styleUrl={results.data?.configuration.styleUrl ?? PARTNER_MAP_DEFAULT_STYLE}
          markers={ready ? markers : []}
          selected={focus}
          onViewport={setViewport}
          onPoint={(latitude, longitude) => {
            if (picking === 'manual') {
              setManual({
                latitude: String(latitude),
                longitude: String(longitude),
                reason: manual?.reason ?? '',
              });
              setPicking(null);
            } else origin(latitude, longitude);
          }}
          picking={picking !== null}
          onSelect={(marker) => {
            if (marker.locations === 1) {
              setSelectedId(marker.id);
              setFocus({ latitude: marker.latitude, longitude: marker.longitude });
            } else if (
              (marker.west === marker.east && marker.north === marker.south) ||
              viewport.zoom >= 20
            )
              origin(marker.latitude, marker.longitude, '0.1');
            else
              setFocus({
                latitude: marker.latitude,
                longitude: marker.longitude,
                zoom: Math.min(22, viewport.zoom + 2),
              });
          }}
        />
        <div className="space-y-2">
          {selected && ready ? (
            <article className="space-y-2 rounded-md border border-border bg-card p-3 text-sm">
              <div className="flex justify-between gap-2">
                <strong>
                  {selected.name} · {selected.number}
                </strong>
                <Button variant="ghost" size="sm" onClick={() => setSelectedId(null)}>
                  {t('common.close')}
                </Button>
              </div>
              <p>{selected.roles.map((role) => t(`partnerMap.${role}`)).join(' · ')}</p>
              <p>
                {selected.label} {selected.recipient}
              </p>
              <p>{selected.address}</p>
              <p>
                {t(`partnerMap.status.${selected.status}`)}
                {selected.source ? ` · ${selected.source}` : ''}
                {selected.geocodedAt ? ` · ${date(selected.geocodedAt)}` : ''}
              </p>
              <p>
                {selected.tags.join(', ')}{' '}
                {selected.industry ? categoryLabel(selected.industry) : ''}
              </p>
              {selected.priceGroup ? (
                <p>
                  {t('partners.filters.priceGroup')}: {selected.priceGroup}
                </p>
              ) : null}
              <p>
                {t('partnerMap.owner')}: {selected.owner ?? '—'}
              </p>
              <p>
                {t('partnerMap.lastContact')}: {date(selected.lastPersonalContactAt)}
              </p>
              <p>
                {t('partnerMap.lastVisit')}: {date(selected.lastVisitAt)}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" asChild>
                  <Link to={`/partners/${selected.customerId}`}>{t('partnerMap.open')}</Link>
                </Button>
                {can('sales_tasks:assign') && features.isPathEnabled('/sales-assistant') ? (
                  <Button variant="outline" onClick={() => setTask(true)}>
                    {t('partnerMap.visit')}
                  </Button>
                ) : null}
                {selected.latitude !== null && selected.longitude !== null ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => origin(selected.latitude ?? 0, selected.longitude ?? 0)}
                    >
                      {t('partnerMap.nearby')}
                    </Button>
                    <Button variant="outline" asChild>
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${selected.latitude},${selected.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Navigation className="h-4 w-4" />
                        {t('partnerMap.navigate')}
                      </a>
                    </Button>
                  </>
                ) : null}
                {can('customer:write') ? (
                  <Button
                    variant="outline"
                    onClick={() =>
                      setManual({
                        latitude: String(selected.latitude ?? ''),
                        longitude: String(selected.longitude ?? ''),
                        reason: '',
                      })
                    }
                  >
                    {t('partnerMap.correct')}
                  </Button>
                ) : null}
              </div>
              {selected.reviewReason ? (
                <p className="text-sm text-muted-foreground">
                  {t(`partnerMap.reviewReasons.${selected.reviewReason}`)}
                </p>
              ) : null}
              {can('customer:write') &&
              ['REVIEW', 'ERROR', 'AMBIGUOUS', 'NOT_FOUND'].includes(selected.status) &&
              selected.source !== 'MANUAL' ? (
                <div className="space-y-2">
                  <label className="flex min-h-11 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={publicAddress}
                      onChange={(event) => setPublicAddress(event.target.checked)}
                    />
                    {t('partnerMap.publicAddress')}
                  </label>
                  <Button
                    disabled={!publicAddress || geocode.isPending}
                    onClick={() => geocode.mutate()}
                  >
                    {t('partnerMap.retryGeocoding')}
                  </Button>
                  {geocode.isError ? <p role="alert">{t('partnerMap.retryBlocked')}</p> : null}
                </div>
              ) : null}
              {manual ? (
                <form
                  className="space-y-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    position.mutate();
                  }}
                >
                  {['latitude', 'longitude', 'reason'].map((key) => (
                    <label key={key} className="block">
                      {t(`partnerMap.${key}`)}
                      <Input
                        required
                        type={key === 'reason' ? 'text' : 'number'}
                        step="any"
                        value={manual[key as keyof typeof manual]}
                        onChange={(event) => setManual({ ...manual, [key]: event.target.value })}
                      />
                    </label>
                  ))}
                  <Button type="button" variant="outline" onClick={() => setPicking('manual')}>
                    {t('partnerMap.pickPosition')}
                  </Button>
                  <Button type="submit" disabled={position.isPending}>
                    {t('common.save')}
                  </Button>
                  {position.isError ? <p role="alert">{t('partnerMap.saveError')}</p> : null}
                </form>
              ) : null}
              <CustomerCareTaskDialog
                customerId={selected.customerId}
                customerName={selected.name}
                activityType={task ? 'VISIT' : null}
                onOpenChange={setTask}
                initialReason={`${t('partnerMap.visit')}: ${selected.address}`}
              />
            </article>
          ) : null}
          {detail.isError ? <p role="alert">{t('partnerMap.loadError')}</p> : null}
          <div
            className="max-h-[50vh] overflow-y-auto rounded border border-border"
            aria-label={t('partnerMap.results')}
          >
            {results.isPending || !ready ? (
              <div className="h-32 animate-pulse bg-muted" />
            ) : results.data?.data.length === 0 ? (
              <p className="p-3 text-sm">{t('partnerMap.empty')}</p>
            ) : (
              results.data?.data.map((location) => (
                <button
                  key={location.id}
                  type="button"
                  aria-pressed={selectedId === location.id}
                  className="block min-h-11 w-full border-b border-border p-3 text-left text-sm hover:bg-muted focus-visible:outline-primary"
                  onClick={() => choose(location)}
                >
                  <strong>{location.name}</strong> · {location.number}
                  <span className="block text-xs text-muted-foreground">
                    {location.label ?? t(`partnerMap.${location.kind}`)} · {location.address}
                  </span>
                  <span className="block text-xs">
                    {t(`partnerMap.status.${location.status}`)}
                    {location.distanceKm !== null
                      ? ` · ${location.distanceKm.toFixed(1)} km (${t('partnerMap.airDistance')})`
                      : ''}
                  </span>
                </button>
              ))
            )}
          </div>
          <div className="flex justify-between gap-2">
            <Button
              variant="outline"
              disabled={params.page <= 1}
              onClick={() => onPage(params.page - 1)}
            >
              {t('common.previous')}
            </Button>
            <span className="self-center text-xs">
              {params.page} / {results.data?.meta.totalPages ?? '—'}
            </span>
            <Button
              variant="outline"
              disabled={!results.data || params.page >= results.data.meta.totalPages}
              onClick={() => onPage(params.page + 1)}
            >
              {t('common.next')}
            </Button>
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        <a href="https://openfreemap.org/" target="_blank" rel="noreferrer">
          OpenFreeMap
        </a>{' '}
        ·{' '}
        <a href="https://openmaptiles.org/" target="_blank" rel="noreferrer">
          OpenMapTiles
        </a>{' '}
        · ©{' '}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
          OpenStreetMap
        </a>{' '}
        · {t('partnerMap.geocodingBy')}{' '}
        <a
          href="https://operations.osmfoundation.org/policies/nominatim/"
          target="_blank"
          rel="noreferrer"
        >
          Nominatim
        </a>{' '}
        ·{' '}
        <a href="https://www.geoapify.com/" target="_blank" rel="noreferrer">
          {t('partnerMap.legacyAttribution')}
        </a>
      </p>
    </section>
  );
}
